import ENDPOINTS, { getTradeableTokensUrl } from '@/config/apiEndpoints';
import APP_CONFIG from '@/config/app';
import { TokenInterface } from '@/interfaces';
import ApiService from '@/services/ApiService';
import { convertScientificToDecimalString } from '@/utils/formatUtils';
import { generateTokenId } from '@/utils/functionalUtils';
import { logError, logInfo } from '@/utils/logger';
import { loadTradeableTokensStub } from '@/utils/stubLoader';
import axios from 'axios';
const preprocessTokens = (
  tokens: (Partial<TokenInterface> & { logoURI?: string })[],
): TokenInterface[] => {
  return tokens
    .filter((token) => {
      const isValid =
        !!token.address && !!token.name && !!token.symbol && !!token.blockchain;
      if (!isValid) {
        logError(
          `Invalid token data skipped: ${JSON.stringify(token)}`,
          'TradeableTokensService.preprocessTokens',
        );
      }
      return isValid;
    })
    .map((token) => {
      const enrichedToken: TokenInterface = {
        id: '',
        name: token.name as string,
        symbol: token.symbol as string,
        address: token.address as string,
        blockchain: token.blockchain as string,
        blockchainDisplayName:
          APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME[
            token.blockchain as keyof typeof APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME
          ] ||
          token.blockchain ||
          'Unknown Blockchain',
        priceInUsd: token.priceInUsd,
        description: token.name || token.symbol,
        logoUri:
          token.logoUri ||
          token.logoURI ||
          APP_CONFIG.DEFAULT_TOKEN_ICON_IMAGE_PATH,
        isNative: token.isNative || false,
      };
      return generateTokenId(enrichedToken);
    });
};
export const fetchTradeableTokens = async (
  network: string,
): Promise<TokenInterface[]> => {
  try {
    let response: any = null;
    const shouldUseStub =
      process.env.NODE_ENV === 'development' &&
      APP_CONFIG.STUBS?.USE_TRADEABLE_TOKENS_STUB;
    if (shouldUseStub) {
      response = await loadTradeableTokensStub();
      if (response) {
        logInfo(
          'Using stub data for tradeable tokens',
          'TradeableTokensService.fetchTokens',
        );
      }
    }
    if (!response) {
      logInfo(
        'Fetching tradeable tokens from API',
        'TradeableTokensService.fetchTokens',
      );
      response = await ApiService.get(getTradeableTokensUrl(network));
    }
    if (response.isBetaRestricted) {
      logInfo(
        'Beta restriction encountered for tradeable tokens',
        'TradeableTokensService.fetchTokens',
        response,
      );
      ApiService.handleUnauthorized();
      return [];
    }
    if (Array.isArray(response)) {
      const preprocessedTokens = preprocessTokens(response);
      logInfo(
        'Fetched and preprocessed tradeable tokens',
        'TradeableTokensService.fetchTokens',
        preprocessedTokens,
      );
      return preprocessedTokens;
    } else {
      logError(
        'Unexpected response format, expected an array',
        'TradeableTokensService.fetchTokens',
        response,
      );
      return [];
    }
  } catch (error: unknown) {
    logError(error, 'TradeableTokensService.fetchTokens');
    if (
      axios.isAxiosError(error) &&
      error.response?.status === 401 &&
      error.response?.data?.errorCode === 'UNAUTHORIZED' &&
      error.response?.data?.errorMessage === 'Not part of beta program'
    ) {
      logInfo(
        'Beta restriction encountered for tradeable tokens',
        'TradeableTokensService.fetchTokens',
        error,
      );
      (error as any).isBetaRestricted = true;
      throw error;
    }
    throw error;
  }
};
const tradeEstimateCache = new Map<
  string,
  { response?: any; timestamp?: number; inProgress?: Promise<any> }
>();
interface TradeEstimateResponse {
  sliverFee: string;
  sliverFeeInUsd: string;
  gasCost: string;
  gasCostInUsd: string;
  bridgeFee?: string;
  bridgeFeeInUsd?: string;
  totalFee: string;
  totalFeeInUsd: string;
  amountOut: string;
  amountOutMin: string;
  isBetaRestricted?: boolean;
  error?: boolean;
  errorMessage?: string;
}
export const getTradeEstimate = async (
  inputToken: { address: string; blockchain: string },
  outputToken: { address: string; blockchain: string },
  amount: string,
  yieldBearingNativeAssetId?: string,
): Promise<TradeEstimateResponse> => {
  try {
    const normalizedAmount = convertScientificToDecimalString(amount);
    const cacheKey = JSON.stringify({
      inputToken,
      outputToken,
      normalizedAmount,
      yieldBearingNativeAssetId,
    });
    logInfo(
      'Preparing to fetch trade estimate',
      'TradeableTokensService.getTradeEstimate',
      { inputToken, outputToken, normalizedAmount, cacheKey },
    );
    const currentTime = Date.now();
    const CACHE_DURATION_MS = APP_CONFIG.LOCAL_CACHE.GAS_ESTIMATE_TTL;
    const cachedEntry = tradeEstimateCache.get(cacheKey);
    if (cachedEntry?.inProgress) {
      logInfo(
        'Request is already in progress, returning the same promise',
        'TradeableTokensService.getTradeEstimate',
        { cacheKey },
      );
      return cachedEntry.inProgress;
    }
    if (
      cachedEntry?.response &&
      currentTime - (cachedEntry.timestamp || 0) < CACHE_DURATION_MS
    ) {
      logInfo(
        'Returning cached trade estimate',
        'TradeableTokensService.getTradeEstimate',
        { cacheKey, cachedResponse: cachedEntry.response },
      );
      return cachedEntry.response;
    }
    const requestBody: any = {
      inputToken,
      outputToken,
      amount: normalizedAmount,
    };
    if (yieldBearingNativeAssetId) {
      requestBody.yieldBearingNativeAssetId = yieldBearingNativeAssetId;
    }
    logInfo(
      'Sending API request for trade estimate',
      'TradeableTokensService.getTradeEstimate',
      { requestBody },
    );
    const inProgressRequest = ApiService.post(
      ENDPOINTS.TRADEABLE_TOKENS_TRADE_ESTIMATE,
      requestBody,
    )
      .then((response) => {
        if (
          response &&
          typeof response === 'object' &&
          (response.error === true ||
            response.isServerError === true ||
            response.errorCode)
        ) {
          const errorDetails = {
            statusCode: response.statusCode || 500,
            errorCode: response.errorCode || 'SERVER_ERROR',
            errorMessage:
              response.errorMessage ||
              'An unexpected error occurred while fetching trade estimate.',
            details: response.details || response.errorMessage,
          };
          logError(
            errorDetails,
            'TradeableTokensService.getTradeEstimate.errorResponse',
          );
          throw errorDetails;
        }
        tradeEstimateCache.set(cacheKey, {
          response,
          timestamp: Date.now(),
          inProgress: undefined,
        });
        logInfo(
          'Fetched new trade estimate and updated cache',
          'TradeableTokensService.getTradeEstimate',
          { cacheKey, response },
        );
        return response;
      })
      .catch((error) => {
        logError(error, 'TradeableTokensService.getTradeEstimate');
        if (
          axios.isAxiosError(error) &&
          error.response?.status === 401 &&
          error.response?.data?.errorCode === 'UNAUTHORIZED' &&
          error.response?.data?.errorMessage === 'Not part of beta program'
        ) {
          logInfo(
            'Beta restriction encountered for trade estimate',
            'TradeableTokensService.getTradeEstimate',
            error,
          );
          ApiService.handleUnauthorized();
          return {
            sliverFee: '0',
            sliverFeeInUsd: '0',
            gasCost: '0',
            gasCostInUsd: '0',
            bridgeFee: '0',
            bridgeFeeInUsd: '0',
            totalFee: '0',
            totalFeeInUsd: '0',
            amountOut: '0',
            amountOutMin: '0',
            isBetaRestricted: true,
          };
        }
        if (axios.isAxiosError(error)) {
          const statusCode = error.response?.status || 500;
          const errorCode = error.response?.data?.errorCode || undefined;
          const errorMessage =
            error.response?.data?.errorMessage ||
            'An unexpected error occurred while fetching trade estimate. Please try again later.';
          const details = error.response?.data?.details || error.message;
          const errorDetails = {
            statusCode,
            ...(errorCode && { errorCode }),
            errorMessage,
            details,
          };
          logError(errorDetails, 'TradeableTokensService.getTradeEstimate');
          throw errorDetails;
        }
        throw new Error('Failed to fetch trade estimate');
      });
    tradeEstimateCache.set(cacheKey, {
      ...cachedEntry,
      inProgress: inProgressRequest,
    });
    return inProgressRequest;
  } catch (error: unknown) {
    logError(error, 'TradeableTokensService.getTradeEstimate');
    if (typeof error === 'object' && error !== null && 'statusCode' in error) {
      throw error;
    }
    throw {
      statusCode: 500,
      errorCode: 'SERVER_ERROR',
      errorMessage: 'Failed to fetch trade estimate',
      details: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};
export const trade = async (
  inputToken: { address: string; blockchain: string },
  outputToken: { address: string; blockchain: string },
  amount: string,
  yieldBearingNativeAssetId?: string,
): Promise<any> => {
  try {
    const normalizedAmount = convertScientificToDecimalString(amount);
    const requestBody: any = {
      inputToken,
      outputToken,
      amount: normalizedAmount,
    };
    if (yieldBearingNativeAssetId) {
      requestBody.yieldBearingNativeAssetId = yieldBearingNativeAssetId;
    }
    logInfo(
      'Sending API request for trade execution',
      'TradeableTokensService.trade',
      { requestBody },
    );
    const response = await ApiService.post(
      ENDPOINTS.TRADEABLE_TOKENS_TRADE,
      requestBody,
    );
    if (response.isBetaRestricted) {
      logInfo(
        'Beta restriction encountered for trade execution',
        'TradeableTokensService.trade',
        response,
      );
      ApiService.handleUnauthorized();
      return {
        isBetaRestricted: true,
        message: 'This feature requires beta access',
      };
    }
    if (
      response &&
      typeof response === 'object' &&
      (response.error === true ||
        response.isServerError === true ||
        response.errorCode)
    ) {
      const errorDetails = {
        statusCode: response.statusCode || 500,
        errorCode: response.errorCode || 'SERVER_ERROR',
        errorMessage:
          response.errorMessage ||
          'An unexpected error occurred while processing the trade.',
        details: response.details || response.errorMessage,
      };
      logError(errorDetails, 'TradeableTokensService.trade.errorResponse');
      throw errorDetails;
    }
    logInfo('Trade executed successfully', 'TradeableTokensService.trade', {
      response,
    });
    return response;
  } catch (error: unknown) {
    logError(error, 'TradeableTokensService.trade');
    if (
      axios.isAxiosError(error) &&
      error.response?.status === 401 &&
      error.response?.data?.errorCode === 'UNAUTHORIZED' &&
      error.response?.data?.errorMessage === 'Not part of beta program'
    ) {
      logInfo(
        'Beta restriction encountered for trade execution',
        'TradeableTokensService.trade',
        error,
      );
      ApiService.handleUnauthorized();
      return {
        isBetaRestricted: true,
        message: 'This feature requires beta access',
      };
    }
    if (axios.isAxiosError(error)) {
      const statusCode = error.response?.status || 500;
      const errorCode = error.response?.data?.errorCode || undefined;
      const errorMessage =
        error.response?.data?.message ||
        'An unexpected error occurred while processing the trade. Please try again later.';
      const details = error.response?.data || error.message;
      const errorDetails = {
        statusCode,
        ...(errorCode && { errorCode }),
        errorMessage,
        details,
      };
      logError(errorDetails, 'TradeableTokensService.trade.axiosError');
      throw errorDetails;
    }
    throw {
      statusCode: 500,
      errorCode: 'SERVER_ERROR',
      errorMessage: 'Failed to execute trade',
      details: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};
export default fetchTradeableTokens;
export const fetchSwappableTokens = async (): Promise<TokenInterface[]> => {
  return await fetchTradeableTokens('ethereum');
};
